import * as React from 'react';

import { ApolloError, ApolloQueryResult } from 'apollo-client';
import idx from 'idx';
import update from 'immutability-helper';
import { isEmpty } from 'lodash';
import { compose, graphql, Query } from 'react-apollo';

import ToastActions from 'actions/ToastActions';
import ErrorReporter from 'app/components/ErrorReporter';
import Notice from 'components/Notice';
import Text from 'components/Text';
import PendingMembershipsTable, {
    GroupMembershipRequest
} from 'groupMembershipRequests/components/PendingMembershipsTable';
import { findMatchingRecord, findMatchingRecordIndex } from 'utils/arrayHelpers';
import nullToUndefined from 'utils/nullToUndefined';

import {
    approveGroupMembershipRequestsVariables
} from 'groupMembershipRequests/containers/mutations/__generated__/approveGroupMembershipRequests';
import {
    denyGroupMembershipRequestsVariables
} from 'groupMembershipRequests/containers/mutations/__generated__/denyGroupMembershipRequests';
import {
    GroupMembershipRequestsQuery, GroupMembershipRequestsQuery_classes,
    GroupMembershipRequestsQuery_classes_groupMembershipRequests_groupMembershipRequests,
    GroupMembershipRequestsQueryVariables
} from 'groupMembershipRequests/containers/queries/__generated__/GroupMembershipRequestsQuery';
import { errorFragment } from 'queries/__generated__/errorFragment';

import APPROVE_GROUP_MEMBERSHIP_REQUESTS_MUTATION from './mutations/ApproveGroupMembershipRequests.gql';
import DENY_GROUP_MEMBERSHIP_REQUESTS_MUTATION from './mutations/DenyGroupMembershipRequests.gql';
import GROUP_MEMBERSHIP_REQUESTS_QUERY from './queries/GroupMembershipRequestsQuery.gql';

type Props = {
  groupUuid: string;
  pageHeader: React.ReactElement;
  pageHeaderHeight: number;
  approveGroupMembershipRequests: (
    { variables }: { variables: approveGroupMembershipRequestsVariables }
  ) => Promise<void>;
  denyGroupMembershipRequests: (
    { variables }: { variables: denyGroupMembershipRequestsVariables }
  ) => Promise<void>;
};

class PendingMembershipsTableContainer extends React.Component<Props> {
  handleApproveClicked = (
    uuid: string,
    refetch: () => Promise<ApolloQueryResult<GroupMembershipRequestsQuery>>
  ) => {
    const { approveGroupMembershipRequests, groupUuid } = this.props;

    return approveGroupMembershipRequests({
      variables: { input: { groupUuid, requestUuids: [uuid] } },
    })
      .then(() => {
        ToastActions.showSuccessToast('Request approved');
        refetch();
        return Promise.resolve();
      })
      .catch(() => {
        ToastActions.showSuccessToast('Unable to approve request. Please try again later.');
      });
  };

  handleDenial = (
    uuid: string,
    refetch: () => Promise<ApolloQueryResult<GroupMembershipRequestsQuery>>
  ) => {
    const { denyGroupMembershipRequests, groupUuid } = this.props;

    return denyGroupMembershipRequests({ variables: { input: { groupUuid, requestUuids: [uuid] } } })
      .then(() => {
        ToastActions.showToast('Request denied', { color: 'shamrock', glyph: 'close' });
        refetch();
        return Promise.resolve();
      })
      .catch(() => {
        ToastActions.showSuccessToast('Unable to deny request. Please try again later.');
      });
  };

  translateMembershipRequests = (
    groupMembershipRequests: GroupMembershipRequestsQuery_classes_groupMembershipRequests_groupMembershipRequests[]
  ): GroupMembershipRequest[] => {
    return groupMembershipRequests.map(groupMembershipRequest => {
      return nullToUndefined(groupMembershipRequest) as GroupMembershipRequest;
    });
  };

  renderError = () => {
    return (
      <ErrorReporter error="PendingMembershipsTableContainer - couldn't load data">
        <Text align="center">
          <Notice color="white">
            Sorry—no data is available at this time. Try refreshing the page.
          </Notice>
        </Text>
      </ErrorReporter>
    );
  };

  render() {
    const { groupUuid, pageHeader, pageHeaderHeight } = this.props;

    return (
      <Query<GroupMembershipRequestsQuery, GroupMembershipRequestsQueryVariables>
        query={GROUP_MEMBERSHIP_REQUESTS_QUERY}
        variables={{
          groupUuid,
          first: 20,
          after: null,
        }}
        fetchPolicy="network-only">
        {({ data, error, fetchMore, loading, refetch }) => {
          if (error) {
            return this.renderError();
          }

          if (loading || !data) {
            return null;
          }

          const group = findMatchingRecord<GroupMembershipRequestsQuery_classes>(
            data.classes,
            groupUuid
          );

          if (!group) {
            return null;
          }

          const {
            groupMembershipRequests,
            pageInfo: { endCursor, hasNextPage },
          } = group.groupMembershipRequests;

          return (
            <PendingMembershipsTable
              groupUuid={groupUuid}
              groupMembershipRequests={this.translateMembershipRequests(groupMembershipRequests)}
              fetchMore={() =>
                fetchMore({
                  variables: {
                    after: endCursor,
                  },
                  updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) {
                      return prev;
                    }

                    const fetchMoreContents = findMatchingRecord(
                      fetchMoreResult.classes,
                      groupUuid
                    );
                    const prevContents = findMatchingRecord(prev.classes, groupUuid);
                    const index = findMatchingRecordIndex(prev.classes, groupUuid);

                    if (
                      !fetchMoreContents ||
                      !prevContents ||
                      index === undefined ||
                      index === null
                    ) {
                      return prev;
                    }

                    return update(prev, {
                      classes: {
                        [index]: {
                          groupMembershipRequests: {
                            groupMembershipRequests: {
                              $set: [
                                ...prevContents.groupMembershipRequests.groupMembershipRequests,
                                ...fetchMoreContents.groupMembershipRequests
                                  .groupMembershipRequests,
                              ],
                            },
                            pageInfo: { $set: fetchMoreContents.groupMembershipRequests.pageInfo },
                          },
                        },
                      },
                    });
                  },
                })
              }
              hasNextPage={hasNextPage}
              loading={loading}
              error={error}
              onApproval={(uuid: string) => this.handleApproveClicked(uuid, refetch)}
              onDenial={(uuid: string) => this.handleDenial(uuid, refetch)}
              pageHeader={pageHeader}
              pageHeaderHeight={pageHeaderHeight}
            />
          );
        }}
      </Query>
    );
  }
}

const withApproveMembershipRequestsMutation = graphql(APPROVE_GROUP_MEMBERSHIP_REQUESTS_MUTATION, {
  name: 'approveGroupMembershipRequests',
});

const withDenyMembershipRequestsMutation = graphql(DENY_GROUP_MEMBERSHIP_REQUESTS_MUTATION, {
  name: 'denyGroupMembershipRequests',
});

export { PendingMembershipsTableContainer };

export default compose(
  withApproveMembershipRequestsMutation,
  withDenyMembershipRequestsMutation
)(PendingMembershipsTableContainer);
